import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, MapPin, Globe, Mail, Building2, Bookmark, Star, Loader2 } from 'lucide-react';
import { notify } from '../utils/notifications';
import { useEventData } from '../context/EventDataContext';
import { useFavorites } from '../hooks/useFavorites';
import { useEventModules } from '../hooks/useEventModules';
import { useApp } from '../context/AppContext';
import { apiAssetUrl } from '../services/apiClient';

interface Exhibitor {
  id: string;
  name: string;
  description?: string | null;
  category?: string | null;
  tier?: string | null;
  booth?: string | null;
  logo_url?: string | null;
  website?: string | null;
  contact_email?: string | null;
  is_featured: boolean;
}

const TIER_COLORS: Record<string, { bg: string; text: string }> = {
  platinum: { bg: 'rgba(226,232,240,0.2)', text: '#CBD5E1' },
  gold:     { bg: 'rgba(251,191,36,0.15)', text: '#F59E0B' },
  silver:   { bg: 'rgba(148,163,184,0.15)', text: '#94A3B8' },
  bronze:   { bg: 'rgba(180,120,80,0.15)', text: '#B47850' },
};

export default function ExhibitorDetailsScreen() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { currentEventId } = useApp();
  const { isEnabled } = useEventModules();
  const { preview, services: { exhibitorService } } = useEventData();

  const [exhibitor, setExhibitor] = useState<Exhibitor | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const saved = exhibitor ? isFavorite(exhibitor.id, 'exhibitor') : false;

  useEffect(() => {
    if (!id || !currentEventId) { setNotFound(true); setLoading(false); return; }
    exhibitorService.getById(id, currentEventId)
      .then((data) => {
        if (!data) setNotFound(true);
        else setExhibitor(data as unknown as Exhibitor);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [currentEventId, id, exhibitorService]);

  if (loading) {
    return (
      <div className="size-full flex items-center justify-center" style={{ background: 'var(--app-bg)' }}>
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--app-accent)' }} />
      </div>
    );
  }

  if (notFound || !exhibitor) {
    return (
      <div className="size-full flex flex-col items-center justify-center gap-4" style={{ background: 'var(--app-bg)' }}>
        <Building2 className="w-12 h-12" style={{ color: 'var(--app-text-subtle)' }} />
        <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>Exhibitor not found</p>
        <button onClick={() => navigate(-1)} className="text-sm" style={{ color: 'var(--app-accent)' }}>Go back</button>
      </div>
    );
  }

  const tierStyle = exhibitor.tier ? (TIER_COLORS[exhibitor.tier.toLowerCase()] ?? null) : null;

  return (
    <div className="size-full flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>
      {/* Header */}
      <div className="app-screen-header flex items-center justify-between px-4 pt-12 pb-4 shrink-0">
        <button
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ background: 'var(--app-surface)' }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
        </button>
        {!preview && <button
          onClick={() => {
            toggleFavorite(exhibitor.id, 'exhibitor');
            notify.info(saved ? 'Removed from saved' : 'Exhibitor saved');
          }}
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ background: 'var(--app-surface)' }}
        >
          <Bookmark
            className="w-4 h-4"
            style={saved ? { fill: 'var(--app-accent)', color: 'var(--app-accent)' } : { color: 'var(--app-text-subtle)' }}
          />
        </button>}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-8">
        {/* Logo + Info */}
        <div className="flex items-center gap-4 mb-5">
          <div
            className="size-16 rounded-xl flex items-center justify-center shrink-0 border bg-white"
            style={{ borderColor: 'var(--app-tag-border)' }}
          >
            {exhibitor.logo_url ? (
              <img src={apiAssetUrl(exhibitor.logo_url) ?? undefined} alt={exhibitor.name} className="w-full h-full object-contain rounded-xl p-1" />
            ) : (
              <span className="text-xl font-semibold" style={{ color: 'var(--app-accent)' }}>{exhibitor.name[0]}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[var(--app-text)] text-xl">{exhibitor.name}</h1>
              {exhibitor.is_featured && (
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 shrink-0" />
              )}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {exhibitor.category && (
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--app-tag-bg)', color: 'var(--app-accent)' }}>
                  {exhibitor.category}
                </span>
              )}
              {exhibitor.tier && tierStyle && (
                <span className="text-xs px-2 py-0.5 rounded-full capitalize" style={{ background: tierStyle.bg, color: tierStyle.text }}>
                  {exhibitor.tier}
                </span>
              )}
              {exhibitor.booth && (
                <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--app-text-muted)' }}>
                  <MapPin className="w-3 h-3" />
                  <span>Booth {exhibitor.booth}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {exhibitor.description && (
          <div className="mb-5">
            <h2 className="text-[var(--app-text)] text-base mb-2">About</h2>
            <p className="text-[var(--app-text-muted)] text-sm leading-relaxed">{exhibitor.description}</p>
          </div>
        )}

        {/* Contact Info */}
        {(exhibitor.website || exhibitor.contact_email) && (
          <div className="rounded-xl p-4 border mb-5" style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}>
            <h2 className="text-[var(--app-text)] text-sm mb-3">Contact</h2>
            <div className="flex flex-col gap-3">
              {exhibitor.website && (
                <button
                  onClick={() => window.open(exhibitor.website!.startsWith('http') ? exhibitor.website! : `https://${exhibitor.website}`, '_blank')}
                  className="flex items-center gap-3 text-sm active:opacity-70 text-left"
                  style={{ color: 'var(--app-text-subtle)' }}
                >
                  <Globe className="w-4 h-4 shrink-0" style={{ color: 'var(--app-accent)' }} />
                  {exhibitor.website}
                </button>
              )}
              {exhibitor.contact_email && (
                <button
                  onClick={() => window.open(`mailto:${exhibitor.contact_email}`, '_blank')}
                  className="flex items-center gap-3 text-sm active:opacity-70 text-left"
                  style={{ color: 'var(--app-text-subtle)' }}
                >
                  <Mail className="w-4 h-4 shrink-0" style={{ color: 'var(--app-accent)' }} />
                  {exhibitor.contact_email}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Find at Conference */}
        {!preview && isEnabled('venue_map') && exhibitor.booth && (
          <button
            onClick={() => navigate('/map')}
            className="w-full h-12 rounded-xl flex items-center justify-center gap-2 text-sm border active:scale-[0.98] transition-transform"
            style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)', color: 'var(--app-text)' }}
          >
            <MapPin className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
            Find at Booth {exhibitor.booth} — View Map
          </button>
        )}
      </div>
    </div>
  );
}
